import { CSSProperties, useState } from "react";
import { useSelector } from "react-redux";
import { Box, CloseButton, Image, Stack, Text } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { selectVolcanoBannerModalSettings } from "../../../redux/selectors/publicSelector";
import { BannerModalPosition, VolcanoBannerImageTextPosition } from "../../../models/setting.model";
import DOMPurify from "dompurify";

const getPositionStyle = (position: BannerModalPosition): CSSProperties => {
    switch (position) {
        case BannerModalPosition.TOP_LEFT:
            return { top: '12vh', left: '2vw' };
        case BannerModalPosition.TOP_CENTER:
            return { top: '12vh', left: '50%', transform: 'translateX(-50%)' };
        case BannerModalPosition.TOP_RIGHT:
            return { top: '12vh', right: '2vw' };
        case BannerModalPosition.CENTER:
            return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
        case BannerModalPosition.BOTTOM_LEFT:
            return { bottom: '3vh', left: '2vw' };
        case BannerModalPosition.BOTTOM_CENTER:
            return { bottom: '3vh', left: '50%', transform: 'translateX(-50%)' };
        case BannerModalPosition.BOTTOM_RIGHT:
        default:
            return { bottom: '3vh', right: '2vw' };
    }
};

const VolcanoBannerSection = () => {
    const [opened, { close }] = useDisclosure(true);
    const [dialogSection, setDialogSection] = useState<"banner" | "contact">("banner");
    const isMobile = useMediaQuery('(max-width: 768px)');
    const volcanoBanner = useSelector(selectVolcanoBannerModalSettings);

    if (dialogSection === "banner") {
        return <Box
            onClick={() => setDialogSection("contact")}
            style={{
                position: 'fixed',
                top: '20vh',
                left: '5vw',
                zIndex: 1000,
                cursor: 'pointer',
            }}
        >
            <Box pos="relative" display="inline-block">
                <Image
                    src={volcanoBanner.imageUrl || '/assets/images/volcano_day_banner.png'}
                    alt="Volcano banner"
                    radius={5}
                    w="auto"
                    h="auto"
                    maw={isMobile ? 290 : 430}
                    mah={isMobile ? 360 : 530}
                    fit="contain"
                />
                {volcanoBanner.showImageText && volcanoBanner.imageText && (
                    <Text
                        size="xl"
                        fw={700}
                        c="white"
                        pos="absolute"
                        top={volcanoBanner.imageTextPosition === VolcanoBannerImageTextPosition.TOP ? 10 : undefined}
                        bottom={volcanoBanner.imageTextPosition === VolcanoBannerImageTextPosition.BOTTOM ? 10 : undefined}
                        left={0}
                        right={0}
                        ta="center"
                    >
                        {volcanoBanner.imageText}
                    </Text>
                )}
            </Box>
        </Box>
    }

    if (!opened || (!volcanoBanner.title && !volcanoBanner.text)) {
        return null;
    }

    return (
        <div
            style={{
                position: 'fixed',
                zIndex: 1100,
                maxWidth: 360,
                width: isMobile ? '80vw' : 350,
                backgroundColor: volcanoBanner.backgroundColor,
                color: volcanoBanner.textColor,
                border: `1px solid ${volcanoBanner.borderColor}`,
                borderRadius: 12,
                maxHeight: '70vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: '24px 16px 0px 16px',
                textAlign: 'center',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
                ...getPositionStyle(volcanoBanner.position),
            }}
        >
            <CloseButton
                onClick={close}
                aria-label="Close banner"
                style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: volcanoBanner.textColor }}
            />
            <div
                style={{
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                    paddingTop: 8,
                    paddingBottom: 8,
                }}
            >
                <Stack align="center" justify="center" gap={6}>
                    {volcanoBanner.title && (
                        <Text fw={700} style={{ color: volcanoBanner.textColor, fontSize: volcanoBanner.fontSize }}>
                            {volcanoBanner.title}
                        </Text>
                    )}
                    {volcanoBanner.text && (
                        <div
                            style={{ color: volcanoBanner.textColor, fontSize: volcanoBanner.fontSize }}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(volcanoBanner.text) }}
                        />
                    )}
                </Stack>
            </div>
        </div>
    )
}

export default VolcanoBannerSection;
